import React from 'react';
import { ReviewQueueEntry } from 'api/DataReview';
import Table from 'Components/DesignSystem/Table/Table';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { TableColumn } from 'types/DesignSystem/Table';
import ReviewQueueStatusPill from './ReviewQueueStatusPill';

interface Props {
  entries: ReviewQueueEntry[];
  onViewLog: (entry: ReviewQueueEntry) => void;
  onRetry: (entry: ReviewQueueEntry) => void;
}

// Map source_tab keys to display labels matching the Figma copy.
const SOURCE_TAB_LABEL: Record<string, string> = {
  sku_matched: 'SKU Matched',
  mpn_upc_matched: 'MPN/UPC Matched',
  unmatched: 'Unmatched',
  ignored_products: 'Ignored Products',
  ignored_updates: 'Ignored Updates',
  processed: 'Processed',
};

// Action keys → human labels (matches the Figma "Action" column copy).
const ACTION_LABEL: Record<string, string> = {
  update: 'Update',
  ignore: 'Ignore',
  restore: 'Restore',
  map_sku: 'Map SKU',
  map_override: 'Map & Update',
  create_new: 'Create New Product',
  search_replace: 'Search & Replace',
  bulk_edit_save: 'Update Attribute Values',
  map_m_to_1: 'Map (M:1)',
  map_1_to_m: 'Map (1:M)',
  // Whole-tab bulk runs (ticket 13765) — mirrors the BE's _ACTION_LABEL map.
  update_all: 'Update All',
  create_all: 'Create All',
};

// Format epoch seconds → "DD-MMM-YYYY, HH:MM UTC" per Figma.
// Rendered in UTC explicitly so a reviewer in any timezone reads the same
// wall-clock the BE wrote to the log lines (which use UTC timestamps too).
// Using getUTC* instead of getDate / getHours etc.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatTs(epoch: number | null | undefined): string {
  if (!epoch) return '-';
  const d = new Date(epoch * 1000);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mmm = MONTHS[d.getUTCMonth()];
  const yyyy = d.getUTCFullYear();
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mi = String(d.getUTCMinutes()).padStart(2, '0');
  return `${dd}-${mmm}-${yyyy}, ${hh}:${mi} UTC`;
}

function formatAffected(entry: ReviewQueueEntry): string {
  const { affected_count: c } = entry;
  if (!c) return '-';
  // Failed rows show their count like any other. The old `status === 'Failed'`
  // early return assumed Failed meant nothing was applied, which is wrong in the
  // two cases that matter: a run stopped mid-flight (its processed rows have
  // really left the tab, and that count is what decides whether to Retry), and a
  // partially-failed run. The backend persists outcome counters per chunk so a
  // killed run keeps credit for what it completed -- blanking it here discarded
  // exactly that. A run that died at row 0 now reads `0 / N`, which says more
  // than a dash.
  if ((c.total || 0) > 1) {
    return `${c.done || 0} / ${c.total}`;
  }
  return String(c.done || c.total || 1);
}

/**
 * The queue grid, on the DS `Table` (2026-09-05, the § 109 RulesQueue shape) —
 * it was a hand-rolled `<table>` on the grey ramp, invisible to the dark theme.
 * Every label map, the UTC formatter and the affected-count rules are verbatim;
 * only the shell moved. The per-row View Log / Retry testids are keyed by the
 * entry uid now — the old `review-queue-reviewqueuetable-button-1`/`-2` were one
 * string duplicated across every row, unusable the moment two rows rendered.
 */
const ReviewQueueTable: React.FC<Props> = ({ entries, onViewLog, onRetry }) => {
  const columns: TableColumn<ReviewQueueEntry>[] = [
    {
      id: 'scope',
      header: 'Scope',
      accessor: (e) => e.scope?.label || '-',
      render: (_value, e) => (
        <div>
          <Typography variant="title" as="div" className="tw-text-ink">
            {e.scope?.label || '-'}
          </Typography>
          {/* Bottom line is the queue entry uid (matches the Compatibility Rules
              Queue pattern). Rendered as-is so the displayed value === the value
              sent to APIs — new rows are stored hyphen-less from `uuid.uuid4().hex`,
              legacy rows keep their hyphens. */}
          {e.uid ? (
            <div
              className="tw-text-ink-secondary tw-text-xs tw-truncate tw-max-w-[260px] tw-font-noto-sans-mono"
              title={e.uid}
            >
              {e.uid}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      id: 'source',
      header: 'Source',
      accessor: (e) => SOURCE_TAB_LABEL[e.source_tab] || e.source_tab || '-',
    },
    {
      id: 'action',
      header: 'Action',
      accessor: (e) => ACTION_LABEL[e.action] || e.action,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      render: (_value, e) => <ReviewQueueStatusPill status={e.status} />,
    },
    {
      id: 'updated',
      header: 'Last Updated',
      accessor: (e) => formatTs(e.updated_at || e.created_at),
    },
    {
      id: 'affected',
      header: 'Affected Count',
      accessor: (e) => formatAffected(e),
    },
    {
      id: 'log',
      header: 'Log',
      accessor: () => '',
      render: (_value, e) => (
        <Button
          variant="tertiary"
          size="sm"
          className="tw-whitespace-nowrap"
          data-testid={`review-queue-view-log-${e.uid}`}
          onClick={() => onViewLog(e)}
        >
          View Log
        </Button>
      ),
    },
    {
      id: 'retry',
      header: '',
      accessor: () => '',
      render: (_value, e) =>
        e.status === 'Failed' ? (
          <Button
            variant="tertiary"
            size="sm"
            data-testid={`review-queue-retry-${e.uid}`}
            onClick={() => onRetry(e)}
          >
            Retry
          </Button>
        ) : null,
    },
  ];

  return (
    <Table
      columns={columns}
      data={entries || []}
      rowKey="uid"
      hoverable={false}
      emptyContent="No tasks in queue"
      data-testid="review-queue-table"
    />
  );
};

export default ReviewQueueTable;
